import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, ShieldCheck, BarChart3, Loader2, ChevronDown, BadgeCheck, ShoppingBag, ArrowUpRight, Crown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { planLabel, isSubscribed } from "@/lib/subscription";
import { US_STATES } from "@/lib/usStates";
import { getConsent, setConsent } from "@/lib/consent";
import AnalyticsSummary from "@/features/admin/components/AnalyticsSummary";
import NotificationToggle from "@/components/NotificationToggle";
import { isOwner } from "@/lib/owner";
import { toast } from "@/components/ui/sonner";

const card = "rounded-2xl border border-gray-200 bg-white dark:border-white/[0.08] dark:bg-charcoal-surface shadow-sm";

function Toggle({ on, onChange, testid, disabled }) {
  return (
    <button type="button" role="switch" aria-checked={on} disabled={disabled} onClick={() => onChange(!on)} data-testid={testid}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${on ? "bg-brand-yellow" : "bg-gray-300 dark:bg-white/20"} disabled:opacity-50`}>
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${on ? "translate-x-5" : "translate-x-0.5"}`} />
    </button>
  );
}

export default function SettingsTab() {
  const { user, saveUserLocation, clearUserLocation } = useAuth();
  const navigate = useNavigate();
  const uid = user?.user_id;
  const c = getConsent(uid);
  const [locConsent, setLocConsent] = useState(c.location);
  const [estOverride, setEstOverride] = useState(c.est);
  const [usageConsent, setUsageConsent] = useState(c.usage);
  const [stateCode, setStateCode] = useState(user?.location?.state_code || "");
  const [zip, setZip] = useState(user?.location?.zip || "");
  const [busy, setBusy] = useState(false);

  const stateName = user?.location?.state_name;
  const taxRate = user?.location?.tax_rate;
  const estMode = !locConsent || estOverride || !user?.location?.state_code;

  const toggleLocation = async (on) => {
    setLocConsent(on);
    setConsent(uid, { location: on });
    if (!on) {
      setBusy(true);
      try { await clearUserLocation?.(); toast.success("Location off — using EST regional averages."); }
      catch { /* ignore */ } finally { setBusy(false); }
    } else {
      toast.success("Location on — set your ZIP & State below for exact tax.");
    }
  };
  const toggleEst = (on) => { setEstOverride(on); setConsent(uid, { est: on }); toast.success(on ? "EST estimates enabled." : "Exact ZIP tax restored."); };
  const toggleUsage = (on) => { setUsageConsent(on); setConsent(uid, { usage: on }); };

  const saveLoc = async () => {
    setBusy(true);
    try {
      const data = await saveUserLocation({ zip: zip.trim(), stateCode });
      toast.success(data?.location?.state_name ? `Saved — exact ${data.location.locality || data.location.state_name} tax applied.` : "Location updated.");
    } catch (e) { toast.error(e?.message || "Enter a valid ZIP or pick your state"); }
    finally { setBusy(false); }
  };

  return (
    <div className="text-gray-900 dark:text-white" data-testid="settings-tab">
      <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-orange">System Hub</p>
      <h2 className="mt-2 text-3xl md:text-4xl font-bold tracking-tighter">Settings</h2>

      {/* Identity / Tier badge */}
      <div className={`${card} mt-6 p-5`} data-testid="settings-identity">
        <div className="flex items-center gap-4">
          <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-yellow to-brand-orange text-lg font-black text-black">{(user?.name || user?.email || "U").charAt(0).toUpperCase()}</span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-base font-black" data-testid="settings-name">{user?.name || "Member"}</p>
            <p className="truncate text-sm text-gray-500 dark:text-zinc-400" data-testid="settings-email">{user?.email}</p>
          </div>
          <span data-testid="settings-tier" className={`inline-flex shrink-0 items-center gap-1 rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-wide ${isSubscribed(user) ? "bg-gradient-to-r from-brand-yellow to-brand-orange text-black" : "bg-gray-100 text-gray-500 dark:bg-white/10 dark:text-zinc-300"}`}><BadgeCheck className="h-3.5 w-3.5" />{planLabel(user)}</span>
        </div>
      </div>

      {/* Plan / Upgrade */}
      <div className={`${card} mt-4 p-5`} data-testid="settings-upgrade">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className="flex items-center gap-2 text-sm font-black uppercase tracking-[0.1em]"><Crown className="h-4 w-4 text-brand-orange" /> Your Plan</h3>
            <p className="mt-1 text-[11px] text-gray-500 dark:text-zinc-400">
              {isSubscribed(user) ? "Unlock every reseller source, full multi-platform reviews, and unlimited Vault saves." : "Free tier — 1 deal source shown. Upgrade for more sources, reviews & Vault saves."}
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate("/pricing")}
          data-testid="settings-open-shop"
          className="mt-4 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-brand-yellow to-brand-orange px-4 text-sm font-black uppercase tracking-wide text-slate-950 transition-transform hover:scale-[1.02]"
        >
          <ShoppingBag className="h-4 w-4" /> {isSubscribed(user) ? "Manage Plan" : "Upgrade Now"} <ArrowUpRight className="h-4 w-4" strokeWidth={3} />
        </button>
      </div>
      <div className={`${card} mt-4 p-5`} data-testid="settings-location">
        <h3 className="flex items-center gap-2 text-sm font-black uppercase tracking-[0.1em]"><MapPin className="h-4 w-4 text-brand-orange" /> Location &amp; Tax</h3>
        <div className={`mt-3 rounded-xl border p-3 text-sm font-bold ${estMode ? "border-brand-yellow/30 bg-brand-yellow/10 text-brand-yellow" : "border-emerald-500/30 bg-emerald-500/10 text-emerald-500"}`} data-testid="settings-tax-status">
          {estMode ? "Estimated Average Mode — using estimated regional averages." : `${stateName} · exact local tax ${taxRate != null ? (taxRate * 100).toFixed(2) + "%" : "—"}`}
        </div>

        <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-hairline dark:bg-white/5">
          <div className="min-w-0"><p className="text-sm font-bold">Allow Location Access</p><p className="text-[11px] text-gray-500 dark:text-zinc-400">Use your ZIP &amp; State for exact local tax.</p></div>
          <Toggle on={locConsent} onChange={toggleLocation} testid="settings-location-toggle" disabled={busy} />
        </div>

        {locConsent && (
          <div className="mt-3 space-y-3" data-testid="settings-location-inputs">
            <div className="relative">
              <select value={stateCode} onChange={(e) => setStateCode(e.target.value)} data-testid="settings-state-select" className="w-full appearance-none rounded-xl border border-gray-300 bg-white px-3 py-3 text-sm font-semibold text-gray-900 outline-none focus:border-brand-yellow dark:border-hairline dark:bg-charcoal dark:text-white">
                <option value="">Select a state…</option>
                {US_STATES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            </div>
            <div className="flex gap-2">
              <input value={zip} onChange={(e) => setZip(e.target.value)} inputMode="numeric" maxLength={5} placeholder="ZIP for exact tax" data-testid="settings-zip-input" className="h-11 flex-1 rounded-xl border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-900 outline-none focus:border-brand-yellow dark:border-hairline dark:bg-charcoal dark:text-white" />
              <button onClick={saveLoc} disabled={busy} data-testid="settings-location-save" className="inline-flex h-11 shrink-0 items-center gap-1.5 rounded-xl bg-gradient-to-r from-brand-yellow to-brand-orange px-4 text-sm font-black uppercase tracking-wide text-black disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</button>
            </div>
          </div>
        )}

        <button
          onClick={() => window.dispatchEvent(new Event("rerun-onboarding"))}
          data-testid="settings-rerun-onboarding"
          className="mt-3 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl border border-brand-yellow/40 bg-brand-yellow/10 px-4 text-sm font-black uppercase tracking-wide text-brand-orange transition-colors hover:bg-brand-yellow/20"
        >
          <MapPin className="h-4 w-4" /> Update Tax Location
        </button>

        <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-hairline dark:bg-white/5">
          <div className="min-w-0"><p className="text-sm font-bold">Use EST Estimates</p><p className="text-[11px] text-gray-500 dark:text-zinc-400">Show estimated regional averages instead of exact tax.</p></div>
          <Toggle on={estOverride} onChange={toggleEst} testid="settings-est-toggle" />
        </div>

        <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-hairline dark:bg-white/5">
          <div className="min-w-0 flex items-center gap-3"><BarChart3 className="h-4 w-4 shrink-0 text-brand-orange" /><div><p className="text-sm font-bold">Allow Usage Data <span className="text-[10px] font-black uppercase text-gray-400">Optional</span></p><p className="text-[11px] text-gray-500 dark:text-zinc-400">Anonymous usage helps improve deal accuracy.</p></div></div>
          <Toggle on={usageConsent} onChange={toggleUsage} testid="settings-usage-toggle" />
        </div>

        <p className="mt-3 flex items-center gap-1.5 text-[11px] text-gray-400 dark:text-zinc-500"><ShieldCheck className="h-3.5 w-3.5" /> Your data is never sold. Stored privately on your account &amp; device.</p>
      </div>

      {/* Push notifications (web + mobile PWA) */}
      <NotificationToggle />

      {/* Developer / owner analytics — strictly gated to the owner identity */}
      {isOwner(user) && (
        <div data-testid="settings-admin">
          <AnalyticsSummary />
        </div>
      )}
    </div>
  );
}
